<template>
  <div class="brand-admin-wrap">
    <div v-if="scope === null" class="ba-boot">Loading…</div>

    <!-- Operator on the email templates page: the one-canvas editor by
         default, on the platform default templates, with a switch to the
         full vendor editor for the same surface and back. -->
    <template v-else-if="scope === 'operator' && initialSurface">
      <div class="ba-editor-switch">
        <template v-if="operatorEditor === 'simple'">
          <span>Simple editor. For the other block types, custom CSS and the rest of the branding tools:</span>
          <button type="button" class="ba-editor-switch-btn" @click="setOperatorEditor('full')">Full editor</button>
        </template>
        <template v-else>
          <span>Full editor.</span>
          <button type="button" class="ba-editor-switch-btn" @click="setOperatorEditor('simple')">Simple editor</button>
        </template>
        <span class="ba-editor-switch-note">Save first: unsaved changes are not carried across.</span>
      </div>
      <TenantTemplateEditor v-if="operatorEditor === 'simple'" :api-base="apiBase" :surface="initialSurface" scope="operator" />
      <BrandOperatorAdmin v-else :api-base="apiBase" :initial-surface="initialSurface" />
    </template>

    <!-- Operator / platform curator on the branding page: the full vendor
         editor (all tabs). -->
    <BrandOperatorAdmin v-else-if="scope === 'operator'" :api-base="apiBase" :initial-surface="initialSurface" />

    <!-- Workspace owner → cut-down, outcome-grouped tenant surfaces. The
         `initialSurface` prop (set by AdminEmailTemplatesPage) picks the
         template editor; its absence (AdminBrandingPage) picks the brand
         identity surface. -->
    <TenantTemplateEditor v-else-if="initialSurface" :api-base="apiBase" :surface="initialSurface" />
    <TenantBrandSurface v-else :api-base="apiBase" />
  </div>
</template>

<script>
import useApi from '@apexl/icarus-core/src/services/api';

/**
 * BrandAdmin (Musubi project override) — a ROLE ROUTER over the branding
 * surface. Both /admin/branding and /admin/email-templates mount NAME
 * `BrandAdmin`; this override decides WHICH editor to show based on the acting
 * brand scope (tenant branding refactor, 2026-07-15):
 *
 *   - operator (GET /branding/surface → scope:'operator', tenant_id resolves
 *     NULL) on /admin/branding (no surface deep-linked) → the full vendor
 *     editor via <BrandOperatorAdmin>, as before.
 *   - operator on /admin/email-templates (a surface deep-linked) → the same
 *     one-canvas <TenantTemplateEditor> a workspace owner gets, told it is in
 *     operator scope so it says these are the platform default templates. A
 *     "Full editor" switch mounts <BrandOperatorAdmin> for the same surface
 *     and "Simple editor" comes back. The choice is remembered for the
 *     browser session only (sessionStorage), never across sessions.
 *   - tenant (workspace owner) → <TenantBrandSurface> for brand identity, or
 *     <TenantTemplateEditor> when a surface was deep-linked (email templates).
 *
 * Deciding on scope (a permission-derived signal from the backend, mirroring
 * BrandingTenantResolver) rather than a URL keeps the two surfaces cleanly
 * separated: chrome/layout/render/governance never render for a tenant because
 * the tenant components simply don't include them.
 */
// sessionStorage key for the operator's choice of editor on the email
// templates page. Values: 'simple' (the default) or 'full'.
const OPERATOR_EDITOR_KEY = 'yorlin.emailTemplates.operatorEditor';

export default {
  name: 'BrandAdmin',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    initialSurface: { type: String, default: '' },
  },
  data() {
    return { api: useApi(), scope: null, operatorEditor: this.rememberedOperatorEditor() };
  },
  mounted() {
    // Resolve which brand scope the current user acts in. On any failure fall
    // back to the tenant surface (least privilege — never expose operator
    // chrome to someone the API wouldn't scope as an operator).
    this.api.get(`${this.apiBase}/branding/surface`)
      .then(res => { this.scope = res?.scope === 'operator' ? 'operator' : 'tenant'; })
      .catch(() => { this.scope = 'tenant'; });
  },
  methods: {
    // Storage can be unavailable (private mode, blocked site data): the
    // choice then lives in this component only.
    rememberedOperatorEditor() {
      try {
        return window.sessionStorage.getItem(OPERATOR_EDITOR_KEY) === 'full' ? 'full' : 'simple';
      } catch (e) { return 'simple'; }
    },
    setOperatorEditor(which) {
      this.operatorEditor = which === 'full' ? 'full' : 'simple';
      try { window.sessionStorage.setItem(OPERATOR_EDITOR_KEY, this.operatorEditor); } catch (e) { /* not remembered */ }
    },
  },
};
</script>

<style scoped>
.ba-boot { color: var(--m-text-tertiary, #787f8c); font-size: 13px; padding: 20px 0; }
.ba-editor-switch {
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  margin-bottom: 16px; padding: 9px 14px;
  border: 1px solid var(--m-border, #e4e7ec); border-radius: 10px;
  background: var(--m-bg-card, #fff);
  color: var(--m-text-secondary, #4a4f5c); font-size: 12.5px;
}
.ba-editor-switch-btn {
  border: 1px solid var(--m-accent, #4f46e5); border-radius: 8px; background: transparent;
  color: var(--m-accent, #4f46e5); font: inherit; font-weight: 600; padding: 4px 12px; cursor: pointer;
}
.ba-editor-switch-btn:hover { background: var(--m-accent-soft, #eef0fe); }
.ba-editor-switch-note { margin-left: auto; color: var(--m-text-tertiary, #787f8c); }
</style>
